// Regla de contraseñas nuevas, la misma en la interfaz (src/domain/password.ts) y en manage-users.
// Sin dependencias de Deno ni del navegador: este archivo se importa desde los dos lados.
//
// Se sigue NIST SP 800-63B: largo mínimo y lista de contraseñas prohibidas en lugar de muchas
// combinaciones obligatorias. Además se exigen letras y números, igual que Supabase Auth con
// password_requirements = "letters_digits" (letras de la A a la Z sin tildes y dígitos 0–9).

export const PASSWORD_MIN_LENGTH = 8;
/** bcrypt (Auth) solo considera 72 bytes; más se rechaza en lugar de truncar en silencio. */
export const PASSWORD_MAX_BYTES = 72;

export const PASSWORD_HINT =
  `Mínimo ${PASSWORD_MIN_LENGTH} caracteres, con letras y números. ` +
  'Una frase de varias palabras con un número es fácil de recordar.';

/** Datos de la cuenta que la contraseña no puede contener. */
export interface PasswordContext {
  name?: string | null | undefined;
  email?: string | null | undefined;
}

// Palabras de la empresa y del sistema: prohibidas en cualquier parte de la contraseña.
const CONTEXT_WORDS = ['imporevers', 'miyama', 'servicioos'];

// Palabras muy usadas en contraseñas. Se rechazan cuando son toda la parte de letras de la clave
// (por ejemplo "Clave2026" o "P@ssw0rd1"). Escritas en minúsculas y sin tildes.
// prettier-ignore
const COMMON_WORDS = new Set([
  'password', 'passw', 'pass', 'contrasena', 'contrasenia', 'clave', 'claves', 'secreto', 'secreta',
  'admin', 'administrador', 'root', 'usuario', 'user', 'login', 'acceso', 'entrar', 'ingreso',
  'bienvenido', 'bienvenida', 'welcome', 'hola', 'holamundo', 'hello', 'test', 'prueba', 'pruebas',
  'demo', 'temporal', 'cambiar', 'cambiame', 'nueva', 'nuevo', 'iloveyou', 'teamo', 'amor', 'amorcito',
  'princesa', 'dios', 'jesus', 'familia', 'futbol', 'football', 'barcelona', 'realmadrid', 'emelec',
  'liga', 'ecuador', 'guayaquil', 'quito', 'cuenca', 'monkey', 'dragon', 'master', 'letmein',
  'sunshine', 'superman', 'batman', 'empresa', 'oficina', 'trabajo', 'taller', 'orden', 'ordenes',
  'servicio', 'tecnico', 'operador', 'soporte', 'sistema',
]);

// Combinaciones de teclado y otras claves conocidas que no se detectan con las reglas de abajo.
const COMMON_PASSWORDS = new Set(['zaq12wsx', '1qaz2wsx', 'qazwsx123', '1q2w3e4r5t', 'q1w2e3r4t5']);

const KEYBOARD_ROWS = ['qwertyuiop', 'asdfghjkl', 'zxcvbnm'];

// Símbolos que se usan en lugar de letras (P@ssw0rd). Los dígitos solo se cambian cuando están entre
// letras, para no convertir el año de "Clave2026" en letras.
const SYMBOL_LETTERS: Record<string, string> = { '@': 'a', $: 's', '!': 'i' };
const DIGIT_LETTERS: Record<string, string> = { '0': 'o', '1': 'i', '3': 'e', '4': 'a', '5': 's', '7': 't' };

/** Minúsculas sin tildes ("Contraseña" → "contrasena"). */
function fold(value: string): string {
  return value.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();
}

function lettersOnly(value: string): string {
  return value.replace(/[^a-z]/g, '');
}

/** Letras de la contraseña después de deshacer los cambios de letras por símbolos y números. */
function decodedLetters(folded: string): string {
  const symbols = folded.replace(/[@$!]/g, (c) => SYMBOL_LETTERS[c] ?? c);
  const digits = symbols.replace(/(?<=[a-z])[013457]+(?=[a-z])/g, (run) =>
    [...run].map((c) => DIGIT_LETTERS[c] ?? c).join(''),
  );
  return lettersOnly(digits);
}

/** ¿Todos los caracteres iguales, o seguidos hacia arriba o hacia abajo ("aaaa", "abcd", "4321")? */
function isRunOrRepeat(value: string): boolean {
  if (value.length < 2) return true;
  const codes = [...value].map((c) => c.charCodeAt(0));
  const step = (codes[1] ?? 0) - (codes[0] ?? 0);
  if (Math.abs(step) > 1) return false;
  return codes.every((code, i) => i === 0 || code - (codes[i - 1] ?? 0) === step);
}

function isKeyboardRun(value: string): boolean {
  if (value.length < 3) return false;
  return KEYBOARD_ROWS.some((row) => row.includes(value) || [...row].reverse().join('').includes(value));
}

function weakLetters(letters: string): boolean {
  return letters.length <= 2 || isRunOrRepeat(letters) || isKeyboardRun(letters) || COMMON_WORDS.has(letters);
}

function weakDigits(digits: string): boolean {
  return digits.length <= 4 || isRunOrRepeat(digits);
}

/** Partes del nombre y del correo con 4 letras o más ("ANA" no cuenta: aparece en muchas palabras). */
function contextParts(context: PasswordContext): string[] {
  const parts = fold(context.name ?? '').split(/[^a-z]+/);
  const local = fold(context.email ?? '').split('@')[0] ?? '';
  parts.push(...local.split(/[^a-z]+/), lettersOnly(local));
  return [...new Set(parts.filter((part) => part.length >= 4))];
}

/**
 * Motivo por el que una contraseña nueva no sirve, o null si cumple la regla. Los mensajes se
 * muestran tal cual al usuario.
 */
export function passwordProblem(password: string, context: PasswordContext = {}): string | null {
  if ([...password].length < PASSWORD_MIN_LENGTH) {
    return `La contraseña debe tener al menos ${PASSWORD_MIN_LENGTH} caracteres.`;
  }
  if (new TextEncoder().encode(password).length > PASSWORD_MAX_BYTES) {
    return 'La contraseña es demasiado larga.';
  }
  if (!/[A-Za-z]/.test(password) || !/[0-9]/.test(password)) {
    return 'La contraseña debe tener letras (de la A a la Z, sin tildes) y números.';
  }

  const folded = fold(password);
  const compact = folded.replace(/[^a-z0-9]/g, '');
  const letters = lettersOnly(folded);
  const decoded = decodedLetters(folded);
  const digits = folded.replace(/[^0-9]/g, '');

  if (CONTEXT_WORDS.some((word) => compact.includes(word) || decoded.includes(word))) {
    return 'La contraseña no puede incluir el nombre de la empresa ni del sistema.';
  }
  if (contextParts(context).some((part) => compact.includes(part) || decoded.includes(part))) {
    return 'La contraseña no puede incluir el nombre ni el correo de la cuenta.';
  }
  if (COMMON_PASSWORDS.has(compact) || ((weakLetters(letters) || COMMON_WORDS.has(decoded)) && weakDigits(digits))) {
    return 'Esa contraseña es muy común y fácil de adivinar. Elige otra.';
  }
  return null;
}
